import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LetterStatus } from '../../types/paydle';

interface GameBoardProps {
  guesses: Array<{
    word: string;
    letterStatuses: LetterStatus[];
  }>;
  currentGuess: string;
  maxAttempts: number;
}

const getTileColor = (status: LetterStatus): string => {
  switch (status) {
    case LetterStatus.CORRECT:
      return 'bg-green-500 border-green-600';
    case LetterStatus.PRESENT:
      return 'bg-yellow-500 border-yellow-600';
    case LetterStatus.ABSENT:
      return 'bg-gray-500 border-gray-600';
    default:
      return 'bg-white border-gray-300';
  }
};

const Tile: React.FC<{
  letter: string;
  status: LetterStatus;
  delay: number;
  isRevealing: boolean;
}> = ({ letter, status, delay, isRevealing }) => {
  const color = getTileColor(status);
  const hasLetter = letter !== '';
  const isCompleted = status !== LetterStatus.UNUSED;

  return (
    <motion.div
      initial={false}
      animate={
        isRevealing
          ? {
              rotateX: [0, 90, 0],
              scale: [1, 1.05, 1],
            }
          : {}
      }
      transition={{
        duration: 0.6,
        delay: delay * 0.15,
        ease: 'easeInOut',
      }}
      className={`w-14 h-14 sm:w-16 sm:h-16 border-2 flex items-center justify-center text-2xl font-bold ${
        isCompleted ? `${color} text-white` : hasLetter ? 'border-gray-500' : 'border-gray-300'
      } transition-all duration-200`}
      style={{
        transformStyle: 'preserve-3d',
      }}
    >
      <motion.span
        animate={
          hasLetter && !isCompleted
            ? {
                scale: [1, 1.1, 1],
              }
            : {}
        }
        transition={{ duration: 0.1 }}
      >
        {letter}
      </motion.span>
    </motion.div>
  );
};

const Row: React.FC<{
  word: string;
  letterStatuses?: LetterStatus[];
  isRevealing: boolean;
}> = ({ word, letterStatuses, isRevealing }) => {
  const letters = (word + '     ').slice(0, 5).split('');

  return (
    <div className="flex gap-1 sm:gap-2">
      {letters.map((letter, index) => (
        <Tile
          key={index}
          letter={letter}
          status={letterStatuses?.[index] || LetterStatus.UNUSED}
          delay={index}
          isRevealing={isRevealing && !!letterStatuses}
        />
      ))}
    </div>
  );
};

export const GameBoard: React.FC<GameBoardProps> = ({ guesses, currentGuess, maxAttempts }) => {
  const emptyRows = maxAttempts - guesses.length - (currentGuess ? 1 : 0);
  const isRevealingLast = guesses.length > 0 && guesses[guesses.length - 1];

  return (
    <div className="flex flex-col gap-1 sm:gap-2 p-4">
      <AnimatePresence>
        {guesses.map((guess, index) => (
          <motion.div
            key={`guess-${index}`}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Row
              word={guess.word}
              letterStatuses={guess.letterStatuses}
              isRevealing={index === guesses.length - 1}
            />
          </motion.div>
        ))}

        {currentGuess && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            <Row word={currentGuess} isRevealing={false} />
          </motion.div>
        )}

        {Array.from({ length: emptyRows }).map((_, index) => (
          <Row key={`empty-${index}`} word="" isRevealing={false} />
        ))}
      </AnimatePresence>
    </div>
  );
};
